/**
 * Referon, `/postbacks/referon`.
 *
 * REG and FTD templates below reproduce the only *confirmed*-production Referon
 * template in POSTBACK_LINKS_REFERENCE.md (the Highspin brand, "подтверждён
 * переданными production-шаблонами"), not the three app-builder templates
 * (backoffice/TMA/Telegram), which already disagree with it on three points —
 * see the pitfalls below and the drift table in
 * docs/solutions/postback-contract-registry.md. This is a deliberate choice,
 * not an oversight: the confirmed template has direct partner-panel provenance,
 * the builder templates do not.
 *
 * There is no confirmed-production template for Referon QUALIFIED_CPA anywhere
 * in the accumulated knowledge; the CPA template here follows the builder
 * consensus (backoffice/TMA/Telegram agree with each other on CPA) for lack of
 * a better source, and is flagged as unconfirmed.
 */
export const referonContract = {
    id: 'referon',
    displayName: 'Referon',
    route: 'referon',
    trackingTagMacro: '${clickid}',
    amountBehavior: 'FTD/CPA amount is NOT populated by any current template — ReferonPostbackDto only maps the ' +
        'backward-compat `amount` field, and no template sends one. dynamic_variable is captured in the raw ' +
        'payload but parameter-mapper.service.ts:mapReferon never reads it; it must never be treated as amount.',
    duplicateParamPolicy: 'array-then-tolerant-last-wins',
    caseSensitiveParams: ['affiliateId'],
    forbiddenAmountSources: ['dynamic_variable'],
    pitfalls: [
        'Our query parameter is always `affiliateId` (capital I). ReferonPostbackDto declares only ' +
            '`affiliateId`; `affiliateid` (lowercase) is a different property to class-validator whitelist:true ' +
            'and is silently dropped before the mapper runs.',
        'dynamic_variable is not a deposit amount and is never mapped to `amount`.',
        'Duplicate subid/uid/tracking_tag/affiliateId in the URL makes Fastify parse an array; tolerated ' +
            'since v5.3.1 (2026-05-26) but never a correct setup — do not rely on it.',
        'KNOWN DIVERGENCE from backoffice/TMA/Telegram builders (documented, not fixed here): those three ' +
            'add `dynamic_variable=${dynamic_variable}` to REG (the confirmed template only has it on FTD), add ' +
            'a redundant `clickId=${clickId}` parameter, and use `${affiliateId}`/`${clickId}` (capital) as the ' +
            'Referon-side macro token where the confirmed Highspin template uses `${affiliateid}`/`${clickid}` ' +
            '(lowercase). If Referon macro substitution is itself case-sensitive, the builder-generated REG/FTD ' +
            'links may silently fail to substitute those two macros on brands configured after this template was ' +
            'introduced. See the open question in docs/solutions/postback-contract-registry.md.',
        'The CPA template has no confirmed-production source; it mirrors builder consensus only.',
    ],
    sourceRefs: [
        'POSTBACK_LINKS_REFERENCE.md:77-146 (Referon section, Highspin confirmed template + operational notes)',
        'apps/api/src/modules/postbacks/dto/referon-postback.dto.ts (affiliateId declared, case-sensitive)',
        'apps/api/src/modules/postbacks/services/parameter-mapper.service.ts:74-110 (mapReferon)',
        'apps/api/src/main.ts:48-49 (whitelist: true)',
    ],
    events: [
        {
            event: 'REG',
            eventValue: 'registration',
            params: [
                { ourParam: 'subid', macro: '${mid}' },
                { ourParam: 'uid', macro: '${customerid}' },
                { ourParam: 'eventType', macro: '${eventType}' },
                { ourParam: 'eventTime', macro: '${eventTime}' },
                { ourParam: 'brandGuid', macro: '${brandGuid}' },
                { ourParam: 'affiliateId', macro: '${affiliateid}' },
                { ourParam: 'tracking_tag', macro: '${clickid}' },
            ],
            notes: [
                'Confirmed production template (Highspin). No dynamic_variable, no duplicate clickId param.',
            ],
        },
        {
            event: 'FTD',
            eventValue: 'ftd',
            params: [
                { ourParam: 'subid', macro: '${mid}' },
                { ourParam: 'uid', macro: '${customerid}' },
                { ourParam: 'dynamic_variable', macro: '${dynamic_variable}' },
                { ourParam: 'eventType', macro: '${eventType}' },
                { ourParam: 'eventTime', macro: '${eventTime}' },
                { ourParam: 'brandGuid', macro: '${brandGuid}' },
                { ourParam: 'currency', macro: '${currency}' },
                { ourParam: 'affiliateId', macro: '${affiliateid}' },
                { ourParam: 'tracking_tag', macro: '${clickid}' },
            ],
            notes: [
                'Confirmed production template (Highspin). dynamic_variable is passed through raw storage only, ' +
                    'never mapped to amount. No amount= param exists on any current Referon FTD template.',
            ],
        },
        {
            event: 'QUALIFIED_CPA',
            eventValue: 'cpa',
            params: [
                { ourParam: 'subid', macro: '${mid}' },
                { ourParam: 'uid', macro: '${customerid}' },
                { ourParam: 'dynamic_variable', macro: '${dynamic_variable}' },
                { ourParam: 'eventType', macro: '${eventType}' },
                { ourParam: 'eventTime', macro: '${eventTime}' },
                { ourParam: 'brandGuid', macro: '${brandGuid}' },
                { ourParam: 'affiliateId', macro: '${affiliateId}' },
                { ourParam: 'clickId', macro: '${clickId}' },
                { ourParam: 'amount', macro: '${amount}' },
                { ourParam: 'currency', macro: '${currency}' },
                { ourParam: 'tracking_tag', macro: '${clickId}' },
            ],
            notes: [
                'UNCONFIRMED: no production-verified Referon CPA template exists; mirrors builder consensus.',
            ],
        },
    ],
};
/**
 * The builder-consensus Referon template, byte-for-byte what backoffice, TMA
 * and the Telegram bot currently emit for all three events (see
 * `test/fixtures.ts`: `backoffice.referon` / `telegram.referon` / `tma.referon`,
 * all three identical). This is an ADDITIVE contract, not a replacement for
 * `referonContract` above — the two intentionally disagree on REG/FTD, and
 * that disagreement is the documented, unresolved drift (row 4,
 * docs/solutions/postback-contract-registry.md). `referonContract` encodes
 * the confirmed-production (Highspin) template; this one exists so a surface
 * migration can reproduce its OWN current output exactly, without silently
 * switching brands over to the confirmed variant mid-migration — that switch
 * is explicitly left open for the project owner to decide, not something a
 * migration should do as a side effect of routing through this package.
 */
export const referonBuilderContract = {
    id: 'referon-builder',
    displayName: 'Referon (builder consensus — backoffice/TMA/Telegram current output)',
    route: 'referon',
    trackingTagMacro: '${clickId}',
    amountBehavior: referonContract.amountBehavior,
    duplicateParamPolicy: 'array-then-tolerant-last-wins',
    caseSensitiveParams: ['affiliateId'],
    forbiddenAmountSources: ['dynamic_variable'],
    pitfalls: [
        'This is the CURRENT output of backoffice/TMA/Telegram, reproduced verbatim, not the ' +
            'confirmed-production template — see `referonContract` for that, and the drift table for why ' +
            'the two disagree on REG/FTD (dynamic_variable on REG, duplicate clickId, macro casing).',
        ...referonContract.pitfalls.filter((p) => !p.startsWith('KNOWN DIVERGENCE')),
    ],
    sourceRefs: [
        'apps/backoffice/src/pages/LinksPage.tsx (TEMPLATES.referon)',
        'apps/tma-analytics/src/pages/LinksPage.tsx (PLATFORM_TEMPLATES["/postbacks/referon"])',
        'apps/api/src/modules/notifications/services/telegram-bot-commands.service.ts (buildPlatformLinks, referon)',
    ],
    events: [
        {
            event: 'REG',
            eventValue: 'registration',
            params: [
                { ourParam: 'subid', macro: '${mid}' },
                { ourParam: 'uid', macro: '${customerid}' },
                { ourParam: 'dynamic_variable', macro: '${dynamic_variable}' },
                { ourParam: 'eventType', macro: '${eventType}' },
                { ourParam: 'eventTime', macro: '${eventTime}' },
                { ourParam: 'brandGuid', macro: '${brandGuid}' },
                { ourParam: 'affiliateId', macro: '${affiliateId}' },
                { ourParam: 'clickId', macro: '${clickId}' },
                { ourParam: 'tracking_tag', macro: '${clickId}' },
            ],
        },
        {
            event: 'FTD',
            eventValue: 'ftd',
            params: [
                { ourParam: 'subid', macro: '${mid}' },
                { ourParam: 'uid', macro: '${customerid}' },
                { ourParam: 'dynamic_variable', macro: '${dynamic_variable}' },
                { ourParam: 'eventType', macro: '${eventType}' },
                { ourParam: 'eventTime', macro: '${eventTime}' },
                { ourParam: 'brandGuid', macro: '${brandGuid}' },
                { ourParam: 'affiliateId', macro: '${affiliateId}' },
                { ourParam: 'clickId', macro: '${clickId}' },
                { ourParam: 'currency', macro: '${currency}' },
                { ourParam: 'tracking_tag', macro: '${clickId}' },
            ],
        },
        {
            event: 'QUALIFIED_CPA',
            eventValue: 'cpa',
            params: [
                { ourParam: 'subid', macro: '${mid}' },
                { ourParam: 'uid', macro: '${customerid}' },
                { ourParam: 'dynamic_variable', macro: '${dynamic_variable}' },
                { ourParam: 'eventType', macro: '${eventType}' },
                { ourParam: 'eventTime', macro: '${eventTime}' },
                { ourParam: 'brandGuid', macro: '${brandGuid}' },
                { ourParam: 'affiliateId', macro: '${affiliateId}' },
                { ourParam: 'clickId', macro: '${clickId}' },
                { ourParam: 'amount', macro: '${amount}' },
                { ourParam: 'currency', macro: '${currency}' },
                { ourParam: 'tracking_tag', macro: '${clickId}' },
            ],
        },
    ],
};
//# sourceMappingURL=referon.js.map